import React from "react";
import { BrowserRouter, Redirect, Route, Switch, useLocation } from "react-router-dom";

import ProtectedRoute from "components/atoms/protected-route/protected-route";
import { useAuth } from "context/auth-context";
import LoginPage from "pages/auth/login";
import NotFoundPage from "pages/global/not-found.page";
import { APP_ROUTE_PAGES } from "navigation/app-route-pages";
import { APP_ROUTER_ROUTES, AppRedirectRoute } from "navigation/route-registry";
import { getAccessiblePortalPath } from "utils/portal-access";

const renderRegistryRedirect = (route: AppRedirectRoute) => (
  <Route
    exact
    key={route.id}
    path={route.path}
    render={({ location }) => {
      const params = new URLSearchParams(location.search);

      if (route.setParams) {
        Object.entries(route.setParams).forEach(([key, value]) => {
          params.set(key, value);
        });
      }

      const search = params.toString();

      return (
        <Redirect
          to={{
            pathname: route.pathname,
            search: search ? `?${search}` : "",
          }}
        />
      );
    }}
  />
);

const AppRoutes: React.FC = () => {
  const { isAuthenticated, portalAccess } = useAuth();
  const location = useLocation();
  const defaultAuthenticatedPath = getAccessiblePortalPath(portalAccess);

  return (
    <Switch key={location.pathname}>
      <Route
        exact
        path="/"
        render={() =>
          isAuthenticated ? <Redirect to={defaultAuthenticatedPath} /> : <Redirect to="/login" />
        }
      />
      <Route
        exact
        path="/login"
        render={() =>
          isAuthenticated ? <Redirect to={defaultAuthenticatedPath} /> : <LoginPage />
        }
      />
      {APP_ROUTER_ROUTES.map((route) => {
        if (route.kind === "redirect") {
          // Switch only inspects direct child `path` props. Wrapping <Route> hides
          // the path and the wrapper matches every URL, so later workspace pages
          // never render.
          return renderRegistryRedirect(route);
        }

        const Component = APP_ROUTE_PAGES[route.id];

        if (!Component) {
          throw new Error(`Missing page component for route "${route.id}"`);
        }

        return (
          <ProtectedRoute
            exact
            component={Component as React.ComponentType<any>}
            key={route.id}
            path={route.path}
          />
        );
      })}
      <Route render={() => (isAuthenticated ? <NotFoundPage /> : <Redirect to="/login" />)} />
    </Switch>
  );
};

const RootRouter: React.FC = () => (
  <BrowserRouter>
    <AppRoutes />
  </BrowserRouter>
);

export default RootRouter;
